/*
 * Copyright 2026 Sony Corporation
 */

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { useState } from 'react';

import { useAuth } from '../../contexts/auth-context';

export interface AddDeviceDialogProps {
  worldId: string;
  open: boolean;
  onClose: () => void;
  onAdded: () => void;
}

export const AddDeviceDialog = ({ worldId, open, onClose, onAdded }: AddDeviceDialogProps) => {
  const { api } = useAuth();
  const [name, setName] = useState('');
  const [model, setModel] = useState('ILCE-7CR');
  const [serial, setSerial] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const submit = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await api.createDevice(worldId, { name, model, serial });
      onAdded();
      onClose();
      setName('');
      setSerial('');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not add the device.');
    } finally {
      setBusy(false);
    }
  };

  const complete = [name, model, serial].every((value) => value.trim() !== '');

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Add device</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" className="mb-4">
            {error}
          </Alert>
        )}
        <DialogContentText variant="body2" className="mb-4">
          The device gets the next free 127.0.0.x address on port 15740, and a running gateway starts a virtual camera
          for it.
        </DialogContentText>
        <Stack spacing={2} className="pt-1">
          <TextField label="Name" value={name} onChange={(event) => setName(event.target.value)} autoFocus required />
          <TextField label="Model" value={model} onChange={(event) => setModel(event.target.value)} required />
          <TextField label="Serial" value={serial} onChange={(event) => setSerial(event.target.value)} required />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={submit} variant="contained" disabled={busy || !complete}>
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};
